<template>
    <div>
        <rkm-form ref="Form">
            <rkm-row>
                <rkm-col cols="6">
                    <rkm-form-select
                        :validations="validations.unidade"
                        :options="form.unidades"
                        :disabled="hasPlantao"
                        text-field="nomeFantasia"
                        value-field="id"
                        v-model="form.unidadeId"
                        label="Unidade"
                        show-default-option
                    />
                </rkm-col>

                <rkm-col cols="6">
                    <rkm-form-select
                        :validations="validations.infraestrutura"
                        :options="infraestruturas"
                        :disabled="hasPlantao"
                        text-field="nome"
                        value-field="id"
                        v-model="form.infraestruturaId"
                        label="Local de atendimento"
                        show-default-option
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="6">
                    <rkm-form-select
                        label="Profissional"
                        v-model="form.usuarioId"
                        :validations="validations.usuario"
                        text-field="nome"
                        value-field="id"
                        :options="usuarios"
                        :disabled="isPlantaoComAtendimentoAgendado"
                        show-default-option
                    />
                </rkm-col>

                <rkm-col cols="6">
                    <rkm-form-select
                        :validations="validations.ocupacao"
                        :options="ocupacoesDisponiveis"
                        text-field="codigoNome"
                        value-field="id"
                        label="CBO"
                        v-model="form.ocupacaoId"
                        :disabled="isPlantaoComAtendimentoAgendado"
                        show-default-option
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="6">
                    <rkm-form-select
                        :options="servicosDisponiveis"
                        :validations="validations.servicos"
                        v-model="form.servicoId"
                        text-field="nome"
                        value-field="id"
                        label="Serviço"
                        default-option-text="Selecione"
                        :disabled="isPlantaoComAtendimentoAgendado"
                        show-default-option
                    />
                </rkm-col>

                <rkm-col cols="6">
                    <rkm-form-select
                        :validations="validations.faixaEtaria"
                        :options="form.faixasEtarias"
                        text-field="nome"
                        value-field="id"
                        label="Perfil de atendimento"
                        v-model="model.faixaEtariaId"
                        default-option-text="Todos"
                        :disabled="isPlantaoComAtendimentoAgendado"
                        show-default-option
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="9">
                    <rkm-form-date-picker
                        label="Período"
                        :start-validations="validations.periodo.start"
                        :end-validations="validations.periodo.end"
                        v-model="model.escala.periodo"
                        range
                        no-default-range-limit
                        :min-date="minDate"
                        :max-date="maxDate"
                        :disabled="hasPlantao || !formPreenchido"
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-time-picker
                        label="Entrada"
                        v-model="model.escala.horaEntrada"
                        :disabled="!formPreenchido"
                        :validations="validations.horaEntrada"
                    />
                </rkm-col>
            </rkm-row>
        </rkm-form>

        <rkm-tabs
            v-model="tabIndexEscala"
            show-background
        >
            <rkm-tab
                title="Escala fixa"
                :title-item-class="{ 'pa-plantao-escala-disabled-tab': disabledEscala || enableEscalaUnica }"
                active
            >
                <rkm-form ref="FormEscalaFixa">
                    <EscalaFixa
                        :form="form"
                        :model="model"
                        :disabled="disabledEscala"
                    />
                </rkm-form>
            </rkm-tab>

            <rkm-tab
                title="Escala dinâmica"
                :title-item-class="{ 'pa-plantao-escala-disabled-tab': disabledEscala || enableEscalaUnica }"
            >
                <rkm-form ref="FormEscalaDinamica">
                    <EscalaDinamica
                        :form="form"
                        :model="model"
                        :disabled="disabledEscala"
                    />
                </rkm-form>
            </rkm-tab>

            <rkm-tab
                title="Escala única"
                :title-item-class="{ 'pa-plantao-escala-disabled-tab': disabledEscala || !enableEscalaUnica }"
            >
                <rkm-form ref="FormEscalaUnica">
                    <EscalaUnica
                        :form="form"
                        :model="model"
                        :disabled="disabledEscala"
                    />
                </rkm-form>
            </rkm-tab>
        </rkm-tabs>
    </div>
</template>

<script>
// Components
import EscalaFixa from './components/escala/fixa/Form'
import EscalaDinamica from './components/escala/dinamica/Form'
import EscalaUnica from './components/escala/unica/Form'

// Mixins.
import { repository, error, data, stringCase, object } from '@/mixins'

// Vuex.
import { mapActions, mapGetters } from 'vuex'

// Moment.
import moment from 'moment'

// Enums.
import tipoEscala from '@/enums/pronto-atendimento/plantao/escala/tipo'
import status from '@/enums/pronto-atendimento/plantao/status'
import servico from '@/enums/cadastro/estabelecimento/servico'

const CBO_GRUPOS_POR_SERVICO = {
    [servico.CONSULTA_MEDICA]: ['2251', '2252', '2253'],
    [servico.ENFERMAGEM]: ['2235', '3222'],
}

export default {
    mixins: [ repository, error, data, stringCase, object ],
    components: {
        EscalaFixa,
        EscalaDinamica,
        EscalaUnica,
    },
    props: {
        id: { type: Number },
    },
    data: () => ({
        repositories: {
            plantao: null,
        },
        minDate: null,
        maxDate: null,
        tabIndexEscala: 0,
        tabsRefsEscala: [
            'FormEscalaFixa',
            'FormEscalaDinamica',
            'FormEscalaUnica',
        ],
        form: {
            unidades: [],
            unidadeId: null,
            infraestruturaId: null,
            usuarioId: null,
            ocupacaoId: null,
            faixasEtarias: [],
            servicos: [],
            servicoId: null,
            diasSemana: [],
            jornadas: [],
            descansos: [],
        },
        model: {
            faixaEtariaId: null,
            escala: {
                tipoId: null,
                periodo: {
                    start: null,
                    end: null
                },
                horaEntrada: null,
                jornadaEmHoras: null,
                diasSemana: [],
                descanso: null,
                horaSaida: null,
                quantidadeAtendimentos: 0,
            }
        },
        additionals: {
            statusId: null
        },
        enums: { tipoEscala, status, servico }
    }),
    computed: {
        validations() {

            return {
                unidade: {
                    required: true
                },
                infraestrutura: {
                    required: true
                },
                usuario: {
                    required: true
                },
                ocupacao: {
                    required: true
                },
                servicos: {
                    required: true
                },
                faixaEtaria: {},
                periodo: {
                    start: { required: true },
                    end: { required: true },
                },
                horaEntrada: {
                    required: true,
                    plantao_date_inicio_less_than_fim: {
                        periodo: {
                            start: this.model.escala.periodo.start,
                            end: this.model.escala.periodo.end,
                        },
                        horaSaida: this.model.escala.horaSaida
                    }
                }
            }
        },
        isPlantaoComAtendimentoAgendado() {

            return this.additionals.statusId === this.enums.status.COM_ATENDIMENTO_AGENDADO
        },
        infraestruturas() {

            if (this.form.unidadeId) {

                return this.form.unidades.find(u => u.id === this.form.unidadeId).infraestruturas
            }

            return []
        },
        usuarios() {

            if (this.form.unidadeId && this.form.infraestruturaId) {

                return this.infraestruturas.find(i => i.id === this.form.infraestruturaId).usuarios
            }

            return []
        },
        ocupacoes() {

            if (this.form.unidadeId && this.form.infraestruturaId && this.form.usuarioId) {

                return this.usuarios.find(u => u.id === this.form.usuarioId).ocupacoes
            }

            return []
        },
        ocupacaoSelecionada() {

            return this.ocupacoes.find(o => o.id === this.form.ocupacaoId) || null
        },
        servicoIdDoCboSelecionado() {

            return this.getServicoIdDoCbo(this.ocupacaoSelecionada?.codigo)
        },
        servicosDisponiveis() {

            return this.form.servicos.filter(item => !this.isServicoIncompativelComCboSelecionado(item.id))
        },
        ocupacoesDisponiveis() {

            return this.ocupacoes.filter(ocupacao => !this.isCboIncompativelComServicoSelecionado(ocupacao))
        },
        hasPlantao() {

            return !!this.id
        },
        formPreenchido() {

            return this.form.unidadeId
                && this.form.infraestruturaId
                && this.form.servicoId
                && this.form.usuarioId
                && this.form.ocupacaoId
        },
        enableEscalaUnica() {

            if (this.model.escala.periodo.start === null || this.model.escala.periodo.end === null) {

                return false
            }

            if (this.model.escala.periodo.start.getTime() === this.model.escala.periodo.end.getTime()) {

                return true
            }

            const diffTime = this.model.escala.periodo.start.getTime() - this.model.escala.periodo.end.getTime()
            const diffInDays = diffTime / (1000 * 60 * 60 * 24)

            return diffInDays < 0 && diffInDays >= -1
        },
        disabledEscala() {

            return this.model.escala.periodo.start === null
                || this.model.escala.periodo.end === null
                || !this.model.escala.horaEntrada
        }
    },
    watch: {
        'infraestruturas': function (infraestruturas) {

            if (infraestruturas.length === 1) {

                this.$nextTick(() => this.form.infraestruturaId = infraestruturas[0].id)
            }
        },
        'servicos': function (servicos) {

            if (servicos.length === 1) {

                this.$nextTick(() => this.form.servicoId = servicos[0].id)
            }
        },
        'usuarios': function (usuarios) {

            if (usuarios.length === 1) {

                this.$nextTick(() => this.form.usuarioId = usuarios[0].id)
            }
        },
        'ocupacoes': function (ocupacoes) {

            if (ocupacoes.length === 1) {

                this.$nextTick(() => this.form.ocupacaoId = ocupacoes[0].id)
            }
        },
        'form.unidadeId': function () {

            this.form.infraestruturaId = null
        },
        'form.infraestruturaId': function () {

            this.form.usuarioId = null
        },
        'form.usuarioId': function () {

            this.form.ocupacaoId = null
        },
        'form.ocupacaoId': function () {

            if (this.form.servicoId && this.isServicoIncompativelComCboSelecionado(this.form.servicoId)) {

                this.form.servicoId = null
            }
        },
        'form.servicoId': function () {

            if (this.form.ocupacaoId && this.isCboIncompativelComServicoSelecionado(this.ocupacaoSelecionada)) {

                this.form.ocupacaoId = null
            }
        },
        enableEscalaUnica(enable, wasEnable) {

            if (enable) {

                this.tabIndexEscala = 2
            }
            else if (wasEnable) {

                this.tabIndexEscala = 0
            }
        },
        tabIndexEscala(index) {

            if (index === 0) {

                this.model.escala.tipoId = this.enums.tipoEscala.FIXA
            }
            if (index === 1) {

                this.model.escala.tipoId = this.enums.tipoEscala.DINAMICA
            }
            if (index === 2) {

                this.model.escala.tipoId = this.enums.tipoEscala.UNICA
            }
        },
        disabledEscala(disabled) {

            if (disabled) {

                this.resetEscala()
            }
        },
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        ...mapGetters(['getLotacao']),
        getServicoIdDoCbo(codigo) {

            if (!codigo) return null

            const grupo = codigo.substring(0, 4)

            return Object.keys(CBO_GRUPOS_POR_SERVICO)
                .map(Number)
                .find(servicoId => CBO_GRUPOS_POR_SERVICO[servicoId].includes(grupo)) ?? null
        },
        isServicoIncompativelComCboSelecionado(servicoId) {

            if (![this.enums.servico.CONSULTA_MEDICA, this.enums.servico.ENFERMAGEM].includes(servicoId)) {

                return false
            }

            return this.servicoIdDoCboSelecionado !== null && this.servicoIdDoCboSelecionado !== servicoId
        },
        isCboIncompativelComServicoSelecionado(ocupacao) {

            if (![this.enums.servico.CONSULTA_MEDICA, this.enums.servico.ENFERMAGEM].includes(this.form.servicoId)) {

                return false
            }

            return this.getServicoIdDoCbo(ocupacao?.codigo) !== this.form.servicoId
        },
        load() {

            this.setLoading()

            const request = this.id
                ? this.repositories.plantao.edit(this.id)
                : this.repositories.plantao.create()

            request
                .then(({ data }) => {

                    this.additionals.statusId = data.additionals.statusId

                    this.minDate = data.minDate
                    this.maxDate = data.maxDate

                    this.form.unidades = data.form.unidades
                    this.form.faixasEtarias = data.form.faixasEtarias
                    this.form.diasSemana = data.form.diasSemana
                    this.form.jornadas = data.form.jornadas
                    this.form.descansos = data.form.descansos
                    this.form.servicos = data.form.servicos

                    if (data.model) {

                        this.form.unidadeId = data.form.unidadeId
                        this.form.servicoId = data.form.servicoId

                        this.$nextTick()
                            .then(() => {

                                this.form.infraestruturaId = data.form.infraestruturaId

                                return this.$nextTick()
                            })
                            .then(() => {

                                this.form.usuarioId = data.form.usuarioId

                                return this.$nextTick()
                            })
                            .then(() => {

                                this.form.ocupacaoId = data.form.ocupacaoId
                                this.model.faixaEtariaId = data.model.faixaEtariaId

                                this.model.escala.periodo.start = moment(
                                    data.model.escala.periodo.start,
                                    'DD/MM/YYYY'
                                ).toDate()

                                this.model.escala.periodo.end = moment(
                                    data.model.escala.periodo.end,
                                    'DD/MM/YYYY'
                                ).toDate()

                                this.model.escala.horaEntrada = data.model.escala.horaEntrada
                                this.model.escala.horaSaida = data.model.escala.horaSaida
                                this.model.escala.quantidadeAtendimentos = data.model.escala.quantidadeAtendimentos
                            })
                    }
                    else {

                        this.setDefaultValues()
                    }
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        save(statusId = null) {

            Promise.all([
                this.$refs.Form.validate(),
                this.$refs[this.tabsRefsEscala[this.tabIndexEscala]].validate(),
            ])
                .then(([formValid, escalaValid]) => {

                    if (!formValid || !escalaValid) return

                    const data = this.prepareData(statusId)

                    if (this.model.escala.tipoId === this.enums.tipoEscala.UNICA) {

                        this.verifyIfIsResting(data.escala.inicio, data.escala.fim, () => {

                            if (this.id && !this.isPlantaoComAtendimentoAgendado) {

                                this.$nextTick().then(() => this.confirmUpdate(() => this.handleSave(data)))
                            }
                            else {

                                this.handleSave(data)
                            }
                        })
                    }
                    else {

                        this.handleSave(data)
                    }
                })
        },
        verifyIfIsResting(inicio, fim, callback) {

            this.setLoading()

            this.repositories.plantao.index({
                not_id: this.isPlantaoComAtendimentoAgendado ? this.id : null,
                usuario_id: this.form.usuarioId,
                resting: { inicio, fim }
            })
                .then(({ data }) => {

                    if (data.length > 0) {

                        this.showConfirmation(
                            'Este profissional está em descanso devido a um plantão realizado. '
                                + 'Deseja realmente continuar?',
                            async () => callback()
                        )
                    }
                    else {

                        callback()
                    }
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        confirmUpdate(callback) {

            this.showConfirmation(
                'Será criado um plantão de escala única com as informações relacionadas '
                    + 'acima e o plantão cadastrado anteriormente será excluído. Deseja continuar?',
                () => this.repositories.plantao.destroy(this.id).then(() => callback()),
                null,
                this.handleError
            )
        },
        handleSave(model) {

            this.setLoading()

            const request = this.hasPlantao && this.isPlantaoComAtendimentoAgendado
                ? this.repositories.plantao.patch(this.id, { escala: model.escala })
                : this.repositories.plantao.store(model)

            request
                .then(({ data }) => {

                    this.showSaveNotification()
                    this.resetForm()

                    this.$emit('save', { plantao: data, updated: !!this.id })
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        resetForm() {

            this.form.unidadeId = null
            this.form.servicoId = null

            this.model.faixaEtariaId = null
            this.model.escala.periodo.start = null
            this.model.escala.periodo.end = null

            this.model.escala.horaEntrada = null

            this.$nextTick()
                .then(() => {

                    this.setDefaultValues()

                    this.tabIndexEscala = 0

                    return this.$nextTick()
                })
                .then(() => {

                    this.$refs.Form.resetValidator()

                    return this.$nextTick()
                })
                .then(() => this.resetEscala())
        },
        setDefaultValues() {

            this.form.unidadeId = this.getLotacao().unidadeId
        },
        resetEscala() {

            this.model.escala.diasSemana = []
            this.model.escala.jornadaEmHoras = null
            this.model.escala.descanso = null
            this.model.escala.horaSaida = null
            this.model.escala.quantidadeAtendimentos = 0

            this.$nextTick(() => {

                this.$refs.FormEscalaFixa.resetValidator()
                this.$refs.FormEscalaDinamica.resetValidator()
                this.$refs.FormEscalaUnica.resetValidator()
            })
        },
        prepareData(statusId) {

            const model = this.clone(this.model)

            const escala = {
                tipoId: model.escala.tipoId,
                inicio: `${this.getFormatedDate(model.escala.periodo.start)} ${model.escala.horaEntrada}`,
                quantidadeAtendimentos: model.escala.quantidadeAtendimentos,
            }

            if (escala.tipoId !== this.enums.tipoEscala.UNICA) {

                escala.jornadaEmHoras = model.escala.jornadaEmHoras
                escala.fim = this.getFormatedDate(model.escala.periodo.end)
            }

            if (escala.tipoId === this.enums.tipoEscala.FIXA) {

                escala.diasSemana = model.escala.diasSemana
            }
            else if (escala.tipoId === this.enums.tipoEscala.UNICA) {

                escala.fim = `${this.getFormatedDate(model.escala.periodo.end)} ${model.escala.horaSaida}`
            }

            const plantao = {
                servicoId:  this.form.servicoId,
                lotacaoId: this.ocupacoes.find(o => o.id === this.form.ocupacaoId).lotacaoId,
                faixaEtariaId: model.faixaEtariaId,
                escala,
                statusId: statusId,
            }

            return this.camelObjectKeysToSnakeRecursive(plantao)
        },
    },
    created() {

        this.repositories.plantao = this.getConectaSusRepository('prontoAtendimento.plantao')

        this.model.escala.tipoId = this.enums.tipoEscala.FIXA

        this.load()
    },
}
</script>

<style>
.pa-plantao-escala-disabled-tab {
    pointer-events: none;
}
</style>